import React, { Component } from 'react';
import { connect } from 'react-redux';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Button } from 'primereact/button';
import { Dialog } from 'primereact/dialog';
import { InputText } from 'primereact/inputtext';
import { Calendar } from 'primereact/calendar';
import { InputNumber } from 'primereact/inputnumber';
import { Toast } from 'primereact/toast';
import { Tooltip } from 'primereact/tooltip';
import { confirmDialog, ConfirmDialog } from 'primereact/confirmdialog';
import Authorizer, { PERMISSIONS } from '../../session/authorizer';
import Authentication from '../../session';
import { baseUrlAdmin } from '../../../store/apiConstants';
import withRouter from '../../lib/withRouter';
import ClassetDropdown from '../../../classetComponents/classetDropDown';
import ClassetInputText from '../../../classetComponents/classetInputText';
import Service from '../../../services';
import { getBoardsData } from '../../../store/actions';
import { userAssignedBoards } from '../../../store/selectors/userAssignedBoards';
import LoadingComponent from '../../loadingComponent';
import AssignExamToStudents from './AssignExamToStudents';
import MarksEntry from './MarksEntry';
import './styles.css';

class SubjectiveExamination extends Component {
    constructor(props) {
        super(props);
        this.state = {
            examinations: [],
            gradingSchemes: [],
            showDialog: false,
            isEditMode: false,
            isLoading: false,
            currentExam: null,
            formData: {
                examName: '',
                gradingSchemeId: '',
                gradingSchemeName: '',
                boardId: '',
                boardName: '',
                classId: '',
                className: '',
                subjectKey: '',
                subjects: []
            },
            classes: [],
            curriculumSubjects: [],
            errors: {},
            selectedRowIndex: null,
            showAssignDialog: false,
            selectedExamination: null,
            showMarksEntryDialog: false,
            selectedExamForMarks: null,
            recalculating: false,
            notifyingParents: false,
            subjectsWithMarks: {} // Track which subjects have marks entered { subjectId: boolean }
        };
        this.service = new Service();
        this.toast = React.createRef();
    }

    componentDidMount() {
        this.props.getBoardsData(true);
        this.fetchExaminations();
        this.fetchGradingSchemes();
    }

    fetchExaminations = async () => {
        try {
            this.setState({ isLoading: true });
            const orgId = localStorage.getItem('orgId');
            const academicYearId = localStorage.getItem('userAcademicYear');

            const response = await this.service.get(
                `${baseUrlAdmin}/subjective-examinations?orgId=${orgId}&academicYearId=${academicYearId}`,
                true
            );

            if (response && response.res && response.res.success) {
                this.setState({
                    examinations: response.res.data || [],
                    isLoading: false
                });
            } else {
                this.setState({ isLoading: false });
            }
        } catch (error) {
            console.error('Error fetching examinations:', error);
            this.setState({ isLoading: false });
            this.toast.current.show({
                severity: 'error',
                summary: 'Error',
                detail: 'Failed to fetch examinations',
                life: 3000
            });
        }
    };

    fetchGradingSchemes = async () => {
        try {
            const orgId = localStorage.getItem('orgId');
            const response = await this.service.get(
                `${baseUrlAdmin}/grading-schemes?orgId=${orgId}`,
                true
            );

            if (response && response.res && response.res.success) {
                this.setState({ gradingSchemes: response.res.data || [] });
            }
        } catch (error) {
            console.error('Error fetching grading schemes:', error);
        }
    };

    openCreateDialog = () => {
        this.setState({
            showDialog: true,
            isEditMode: false,
            formData: {
                examName: '',
                gradingSchemeId: '',
                gradingSchemeName: '',
                boardId: '',
                boardName: '',
                classId: '',
                className: '',
                subjectKey: '',
                subjects: []
            },
            classes: [],
            curriculumSubjects: [],
            errors: {},
            subjectsWithMarks: {} // Reset for new exam
        });
    };

    openEditDialog = (exam) => {
        // Format dates for Calendar component
        const formattedSubjects = exam.subjects.map(subject => ({
            ...subject,
            examDate: new Date(subject.examDate),
            deadlineForMarksEntry: new Date(subject.deadlineForMarksEntry)
        }));

        this.setState({
            showDialog: true,
            isEditMode: true,
            currentExam: exam,
            formData: {
                examName: exam.examName,
                gradingSchemeId: exam.gradingSchemeId,
                gradingSchemeName: exam.gradingSchemeName || '',
                boardId: exam.boardId,
                boardName: exam.boardName,
                classId: exam.classId,
                className: exam.className,
                subjectKey: exam.subjectKey || '',
                subjects: formattedSubjects
            },
            errors: {}
        }, () => {
            // Load classes for the selected board, then fetch subjects in callback
            this.onChangeBoard(exam.boardId, true, () => {
                // Fetch curriculum subjects after board/classes are loaded
                this.fetchSubjects();
            });
            // Check which subjects have marks entered
            this.checkSubjectsWithMarks(exam.examId);
        });
    };

    checkSubjectsWithMarks = async (examId) => {
        try {
            const { subjects } = this.state.formData;
            if (!subjects || subjects.length === 0) {
                return;
            }

            const subjectIds = subjects.map(s => s.subjectId).join(',');
            const response = await this.service.get(
                `${baseUrlAdmin}/subjective-examinations/check-marks/${examId}?subjectIds=${subjectIds}`,
                true
            );

            if (response && response.res && response.res.success) {
                this.setState({
                    subjectsWithMarks: response.res.data
                });
            }
        } catch (error) {
            console.error('Error checking subjects with marks:', error);
            // If error, allow deletion (safer to show delete button than hide it incorrectly)
            this.setState({
                subjectsWithMarks: {}
            });
        }
    };

    closeDialog = () => {
        this.setState({
            showDialog: false,
            isEditMode: false,
            currentExam: null,
            formData: {
                examName: '',
                gradingSchemeId: '',
                gradingSchemeName: '',
                boardId: '',
                boardName: '',
                classId: '',
                className: '',
                subjectKey: '',
                subjects: []
            },
            classes: [],
            curriculumSubjects: [],
            errors: {},
            subjectsWithMarks: {} // Reset
        });
    };

    onChangeBoard = (boardId, skipSubjectsReset = false, callback) => {
        const selectedBoard = this.props.boards.find(board => board.boardId === boardId);
        const classes = [];

        if (selectedBoard && selectedBoard.classes && selectedBoard.classes.length > 0) {
            selectedBoard.classes.forEach(each => {
                classes.push({
                    label: each.className,
                    value: each.classId,
                    groupId: each?.groups ? each?.groups[0]?.groupId : ''
                });
            });
        }

        this.setState({
            classes,
            formData: {
                ...this.state.formData,
                boardId,
                boardName: selectedBoard?.boardName || '',
                classId: skipSubjectsReset ? this.state.formData.classId : '',
                className: skipSubjectsReset ? this.state.formData.className : '',
                subjects: skipSubjectsReset ? this.state.formData.subjects : []
            },
            curriculumSubjects: skipSubjectsReset ? this.state.curriculumSubjects : []
        }, callback);
    };

    onChangeClass = async (classId) => {
        const selectedClass = this.state.classes.find(cls => cls.value === classId);

        this.setState({
            formData: {
                ...this.state.formData,
                classId,
                className: selectedClass?.label || '',
                subjects: []
            }
        }, () => this.fetchSubjects());
    };

    fetchSubjects = async () => {
        const { boardId, classId } = this.state.formData;
        const selectedClass = this.state.classes.find(cls => cls.value === classId);
        const groupId = selectedClass?.groupId || 'NO_GROUP';

        console.log('fetchSubjects called with:', {
            boardId,
            classId,
            selectedClass,
            groupId,
            classesAvailable: this.state.classes.length
        });

        if (boardId && classId) {
            this.setState({ isLoading: true });
            try {
                const response = await this.service.get(
                    `${baseUrlAdmin}/subject?boardId=${boardId}&classId=${classId}&groupId=${groupId}`,
                    true
                );

                if (response && response.res && response.res.Item && response.res.Item.subjects) {
                    // Auto-populate subjects with default values (only if not editing)
                    const subjects = this.state.isEditMode ? this.state.formData.subjects : response.res.Item.subjects.map(subject => ({
                        subjectId: subject.subjectId,
                        subjectName: subject.subjectName,
                        syllabus: subject.syllabus || '',
                        examDate: null,
                        examDuration: 60,
                        maxMarks: 100,
                        passMarks: 40,
                        deadlineForMarksEntry: null
                    }));

                    this.setState({
                        curriculumSubjects: response.res.Item.subjects,
                        formData: {
                            ...this.state.formData,
                            subjectKey: response.res.Item.subjectKey || '',
                            subjects: subjects
                        },
                        isLoading: false
                    });
                } else {
                    this.setState({
                        curriculumSubjects: [],
                        isLoading: false
                    });
                    this.toast.current.show({
                        severity: 'info',
                        summary: 'Info',
                        detail: 'No subjects found for this board and class',
                        life: 3000
                    });
                }
            } catch (error) {
                console.error('Error fetching subjects:', error);
                this.setState({ isLoading: false });
                this.toast.current.show({
                    severity: 'error',
                    summary: 'Error',
                    detail: 'Failed to fetch subjects',
                    life: 3000
                });
            }
        }
    };

    addSubjectRow = () => {
        const newSubject = {
            subjectId: '',
            subjectName: '',
            syllabus: '',
            examDate: null,
            examDuration: 60,
            maxMarks: 100,
            passMarks: 40,
            deadlineForMarksEntry: null
        };

        this.setState({
            formData: {
                ...this.state.formData,
                subjects: [...this.state.formData.subjects, newSubject]
            }
        });
    };

    removeSubjectRow = (index) => {
        const subjects = [...this.state.formData.subjects];
        subjects.splice(index, 1);

        this.setState({
            formData: {
                ...this.state.formData,
                subjects
            }
        });
    };

    canDeleteSubject = (subjectId) => {
        const { isEditMode, currentExam, subjectsWithMarks } = this.state;

        // If creating new exam, can always delete
        if (!isEditMode || !currentExam) {
            return true;
        }

        // If editing, check if marks have been entered
        return !subjectsWithMarks[subjectId];
    };

    handleSubjectSelect = (index, subjectId) => {
        const selectedSubject = this.state.curriculumSubjects.find(s => s.subjectId === subjectId);

        if (selectedSubject) {
            const subjects = [...this.state.formData.subjects];
            subjects[index] = {
                ...subjects[index],
                subjectId: selectedSubject.subjectId,
                subjectName: selectedSubject.subjectName,
                syllabus: selectedSubject.syllabus || ''
            };

            this.setState({
                formData: {
                    ...this.state.formData,
                    subjects
                }
            });
        }
    };

    getAvailableSubjects = (currentIndex) => {
        const { curriculumSubjects } = this.state;
        const { subjects } = this.state.formData;

        // Get all selected subject IDs except the current row
        const selectedSubjectIds = subjects
            .map((s, idx) => idx !== currentIndex ? s.subjectId : null)
            .filter(id => id !== null && id !== '');

        // Filter out already selected subjects
        return curriculumSubjects.filter(
            subject => !selectedSubjectIds.includes(subject.subjectId)
        );
    };

    canAddMoreSubjects = () => {
        const { curriculumSubjects } = this.state;
        const { subjects } = this.state.formData;

        // If curriculum subjects not loaded yet, enable the button
        // (it will be properly enabled/disabled once curriculum loads)
        if (!curriculumSubjects || curriculumSubjects.length === 0) {
            return true;
        }

        // Can add if there are curriculum subjects not yet in the exam
        const selectedSubjectIds = subjects
            .map(s => s.subjectId)
            .filter(id => id !== null && id !== '');

        return curriculumSubjects.length > selectedSubjectIds.length;
    };

    getAddSubjectTooltip = () => {
        const { curriculumSubjects } = this.state;
        const { subjects } = this.state.formData;

        // If curriculum not loaded yet
        if (!curriculumSubjects || curriculumSubjects.length === 0) {
            return "Add a new subject";
        }

        const selectedSubjectIds = subjects
            .map(s => s.subjectId)
            .filter(id => id !== null && id !== '');

        if (curriculumSubjects.length <= selectedSubjectIds.length) {
            return "All subjects are already added";
        }

        return "Add a new subject";
    };

    onChangeGradingScheme = (gradingSchemeId) => {
        const selectedScheme = this.state.gradingSchemes.find(
            scheme => scheme.gradingSchemeId === gradingSchemeId
        );

        this.setState({
            formData: {
                ...this.state.formData,
                gradingSchemeId,
                gradingSchemeName: selectedScheme?.name || ''
            }
        });
    };

    updateSubjectField = (index, field, value) => {
        const subjects = [...this.state.formData.subjects];
        subjects[index] = {
            ...subjects[index],
            [field]: value
        };

        this.setState({
            formData: {
                ...this.state.formData,
                subjects
            }
        });
    };

    validateForm = () => {
        const errors = {};
        const { examName, gradingSchemeId, boardId, classId, subjects } = this.state.formData;

        if (!examName || examName.trim() === '') {
            errors.examName = 'Examination name is required';
        }

        if (!gradingSchemeId) {
            errors.gradingSchemeId = 'Grading scheme is required';
        }

        if (!boardId) {
            errors.boardId = 'Board is required';
        }

        if (!classId) {
            errors.classId = 'Class is required';
        }

        if (!subjects || subjects.length === 0) {
            errors.subjects = 'At least one subject is required';
        } else {
            const subjectErrors = [];
            subjects.forEach((subject, index) => {
                const subError = {};

                // Check if subject is selected (for manually added rows)
                if (!subject.subjectId || subject.subjectId === '') {
                    subError.subjectId = 'Please select a subject';
                }

                if (!subject.examDate) {
                    subError.examDate = 'Exam date is required';
                }

                if (!subject.deadlineForMarksEntry) {
                    subError.deadlineForMarksEntry = 'Deadline is required';
                } else if (subject.examDate && subject.deadlineForMarksEntry) {
                    const examDate = new Date(subject.examDate);
                    const deadline = new Date(subject.deadlineForMarksEntry);
                    if (deadline <= examDate) {
                        subError.deadlineForMarksEntry = 'Deadline must be after exam date';
                    }
                }

                if (subject.passMarks > subject.maxMarks) {
                    subError.passMarks = 'Pass marks cannot exceed max marks';
                }

                if (Object.keys(subError).length > 0) {
                    subjectErrors[index] = subError;
                }
            });

            if (subjectErrors.length > 0) {
                errors.subjectErrors = subjectErrors;
            }
        }

        this.setState({ errors });
        return Object.keys(errors).length === 0;
    };

    saveExamination = async () => {
        if (!this.validateForm()) {
            this.toast.current.show({
                severity: 'error',
                summary: 'Validation Error',
                detail: 'Please fix the errors in the form',
                life: 3000
            });
            return;
        }

        this.setState({ isLoading: true });

        try {
            const orgId = localStorage.getItem('orgId');
            const academicYearId = localStorage.getItem('userAcademicYear');

            const payload = {
                ...this.state.formData,
                orgId,
                academicYearId
            };

            let response;
            if (this.state.isEditMode) {
                response = await this.service.put(
                    `${baseUrlAdmin}/subjective-examinations/${this.state.currentExam.examId}`,
                    payload,
                    true
                );
            } else {
                response = await this.service.post(
                    `${baseUrlAdmin}/subjective-examinations`,
                    payload,
                    true
                );
            }

            if (response && response.res && response.res.success) {
                this.toast.current.show({
                    severity: 'success',
                    summary: 'Success',
                    detail: `Examination ${this.state.isEditMode ? 'updated' : 'created'} successfully`,
                    life: 3000
                });
                this.closeDialog();
                this.fetchExaminations();
            } else {
                throw new Error(response.res?.message || response.message || 'Failed to save examination');
            }
        } catch (error) {
            console.error('Error saving examination:', error);
            this.toast.current.show({
                severity: 'error',
                summary: 'Error',
                detail: error.message || 'Failed to save examination',
                life: 3000
            });
        } finally {
            this.setState({ isLoading: false });
        }
    };

    dateTemplate = (rowData) => {
        return new Date(rowData.createdAt).toLocaleDateString('en-IN', {
            year: 'numeric',
            month: 'short',
            day: 'numeric'
        });
    };

    actionsTemplate = (rowData) => {
        const { recalculating } = this.state;

        return (
            <div style={{ display: 'flex', alignItems: 'center', gap: 0 }}>
                <Button
                    icon="pi pi-pencil"
                    className="p-button-rounded p-button-text"
                    onClick={() => this.openEditDialog(rowData)}
                    tooltip="Edit"
                    tooltipOptions={{ position: 'top' }}
                />
                <Button
                    icon="pi pi-users"
                    className="p-button-rounded p-button-text"
                    style={{ marginLeft: '2px' }}
                    onClick={() => this.setState({ showAssignDialog: true, selectedExamination: rowData })}
                    tooltip="Assign to Students"
                    tooltipOptions={{ position: 'top' }}
                />
                <Button
                    icon="pi pi-list"
                    className="p-button-rounded p-button-text"
                    style={{ marginLeft: '2px' }}
                    onClick={() => this.setState({ showMarksEntryDialog: true, selectedExamForMarks: rowData })}
                    tooltip="Marks Entry"
                    tooltipOptions={{ position: 'top' }}
                />
                <Button
                    icon="pi pi-calculator"
                    className="p-button-rounded p-button-text"
                    style={{ marginLeft: '2px' }}
                    onClick={() => this.handleRecalculate(rowData)}
                    disabled={recalculating}
                    tooltip="Recalculate Results"
                    tooltipOptions={{ position: 'top' }}
                />
                <Button
                    icon="pi pi-send"
                    className="p-button-rounded p-button-text"
                    style={{ marginLeft: '2px' }}
                    onClick={() => this.handleNotifyParents(rowData)}
                    tooltip="Notify Parents about Results"
                    tooltipOptions={{ position: 'top' }}
                />
            </div>
        );
    };

    handleAssignDialogClose = () => {
        this.setState({
            showAssignDialog: false,
            selectedExamination: null
        });
        // Refresh examinations to get updated assignments
        this.fetchExaminations();
    };

    handleMarksEntryDialogClose = () => {
        this.setState({
            showMarksEntryDialog: false,
            selectedExamForMarks: null
        });
    };

    handleRecalculate = async (rowData) => {
        try {
            this.setState({ recalculating: true });

            const response = await this.service.post(
                `${baseUrlAdmin}/subjective-examinations/recalculate/${rowData.examId}`,
                {
                    calculatedBy: localStorage.getItem('userId')
                },
                true
            );

            if (response && response.res && response.res.success) {
                this.toast.current.show({
                    severity: 'success',
                    summary: 'Success',
                    detail: 'Results recalculated successfully',
                    life: 3000
                });
                this.fetchExaminations();
            } else {
                this.toast.current.show({
                    severity: 'error',
                    summary: 'Error',
                    detail: response?.res?.message || 'Failed to recalculate results',
                    life: 3000
                });
            }
        } catch (error) {
            console.error('Error recalculating results:', error);
            this.toast.current.show({
                severity: 'error',
                summary: 'Error',
                detail: 'Failed to recalculate results',
                life: 3000
            });
        } finally {
            this.setState({ recalculating: false });
        }
    };

    handleNotifyParents = (rowData) => {
        confirmDialog({
            message: `Send result notifications for "${rowData.examName}" to parents via configured channels?`,
            header: 'Notify Parents',
            icon: 'pi pi-send',
            acceptClassName: 'p-button-primary',
            rejectClassName: 'p-button-outlined',
            accept: async () => {
                try {
                    this.setState({ notifyingParents: true });

                    const response = await this.service.post(
                        `${baseUrlAdmin}/notification-workflows/exam-result/send`,
                        {
                            examId: rowData.examId,
                            examType: 'subjective'
                        },
                        true
                    );

                    if (response && response.status) {
                        this.toast.current.show({
                            severity: 'success',
                            summary: 'Success',
                            detail: `Notifications sent to ${response.res?.data?.totalStudents || 0} students`,
                            life: 3000
                        });
                    } else {
                        this.toast.current.show({
                            severity: 'error',
                            summary: 'Error',
                            detail: response?.message || 'Failed to send notifications',
                            life: 3000
                        });
                    }
                } catch (error) {
                    console.error('Error sending notifications:', error);
                    this.toast.current.show({
                        severity: 'error',
                        summary: 'Error',
                        detail: 'Failed to send notifications',
                        life: 3000
                    });
                } finally {
                    this.setState({ notifyingParents: false });
                }
            }
        });
    };

    render() {
        const { examinations, gradingSchemes, showDialog, formData, classes, curriculumSubjects, errors, showAssignDialog, selectedExamination, showMarksEntryDialog, selectedExamForMarks } = this.state;

        const dialogFooter = (
            <div className="dialog-footer">
                <Button
                    label="Cancel"
                    icon="pi pi-times"
                    className="p-button-text"
                    onClick={this.closeDialog}
                />
                <Button
                    label="Save"
                    icon="pi pi-check"
                    onClick={this.saveExamination}
                    disabled={this.state.isLoading}
                />
            </div>
        );

        return (
            <div className="subjective-exam-container">
                <Toast ref={this.toast} />
                <ConfirmDialog />
                {this.state.isLoading && <LoadingComponent />}

                <div className="subjective-exam-header">
                    <h2>Subjective Examination</h2>
                    <Button
                        label="Add Exam"
                        icon="pi pi-plus"
                        className="p-button-primary create-button"
                        onClick={this.openCreateDialog}
                    />
                </div>

                <div className="exam-table-card">
                    <DataTable
                        value={examinations}
                        paginator
                        rows={10}
                        emptyMessage="No examinations found"
                        responsiveLayout="scroll"
                    >
                        <Column field="examName" header="Examination Name" sortable />
                        <Column
                            field="gradingSchemeName"
                            header="Grading Scheme"
                            body={(rowData) => {
                                const scheme = gradingSchemes.find(s => s.gradingSchemeId === rowData.gradingSchemeId);
                                return scheme?.name || rowData.gradingSchemeId;
                            }}
                            sortable
                        />
                        <Column field="boardName" header="Board" sortable />
                        <Column field="className" header="Grade" sortable />
                        <Column
                            header="Subjects Count"
                            body={(rowData) => rowData.subjects?.length || 0}
                            sortable
                        />
                        <Column header="Created Date" body={this.dateTemplate} sortable />
                        <Column header="Actions" body={this.actionsTemplate} style={{ width: '180px' }} />
                    </DataTable>
                </div>

                <Dialog
                    header={`${this.state.isEditMode ? 'Edit' : 'Create'} Subjective Examination`}
                    visible={showDialog}
                    style={{ width: '90vw' }}
                    onHide={this.closeDialog}
                    footer={dialogFooter}
                >
                    <div className="form-section">
                        <div className="grid">
                            <div className="col-3">
                                <ClassetInputText
                                    id="examName"
                                    label="Examination Name"
                                    required={true}
                                    value={formData.examName}
                                    onChange={(e) =>
                                        this.setState({
                                            formData: { ...formData, examName: e.target.value }
                                        })
                                    }
                                    className="w-full"
                                    placeholder="Enter examination name"
                                />
                                {errors.examName && <small className="error-text">{errors.examName}</small>}
                            </div>

                            <div className="col-3">
                                <ClassetDropdown
                                    id="gradingScheme"
                                    label="Grading Scheme"
                                    required={true}
                                    value={formData.gradingSchemeId}
                                    options={gradingSchemes}
                                    onChange={(e) => this.onChangeGradingScheme(e.value)}
                                    optionLabel="name"
                                    optionValue="gradingSchemeId"
                                    placeholder="Select grading scheme"
                                    className={errors.gradingSchemeId ? 'field-error' : ''}
                                />
                                {errors.gradingSchemeId && <small className="error-text">{errors.gradingSchemeId}</small>}
                            </div>

                            <div className="col-3">
                                <ClassetDropdown
                                    label="Board"
                                    required={true}
                                    placeholder="Select Board"
                                    options={this.props.boards}
                                    value={formData.boardId}
                                    onChange={(e) => this.onChangeBoard(e.value)}
                                    optionLabel="boardName"
                                    optionValue="boardId"
                                    className={errors.boardId ? 'field-error' : ''}
                                />
                                {errors.boardId && <small className="error-text">{errors.boardId}</small>}
                            </div>

                            <div className="col-3">
                                <ClassetDropdown
                                    label="Grade"
                                    required={true}
                                    placeholder="Select Grade"
                                    options={classes}
                                    value={formData.classId}
                                    onChange={(e) => this.onChangeClass(e.value)}
                                    className={errors.classId ? 'field-error' : ''}
                                />
                                {errors.classId && <small className="error-text">{errors.classId}</small>}
                            </div>
                        </div>
                    </div>

                    {/* Subjects Section */}
                    {formData.classId && (curriculumSubjects.length > 0 || formData.subjects.length > 0) && (
                        <div className="subjects-table">
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
                                <div className="form-section-title">Subjects Details</div>
                                <Button
                                    label="Add Subject"
                                    icon="pi pi-plus"
                                    className="p-button-sm"
                                    onClick={this.addSubjectRow}
                                    type="button"
                                    disabled={!this.canAddMoreSubjects()}
                                    tooltip={this.getAddSubjectTooltip()}
                                    tooltipOptions={{ position: 'top' }}
                                />
                            </div>
                            {formData.subjects.length > 0 ? (
                                <div className="subjects-table-container">
                                    <DataTable
                                        value={formData.subjects}
                                        responsiveLayout="scroll"
                                        stripedRows={false}
                                    >
                                        <Column
                                            header="S.No"
                                            body={(data, options) => options.rowIndex + 1}
                                            style={{ width: '60px' }}
                                        />
                                        <Column
                                            header="Subject *"
                                            body={(rowData, options) => {
                                                // If subject is already selected (auto-populated or previously selected), show static text
                                                if (rowData.subjectId && rowData.subjectId !== '') {
                                                    return <span>{rowData.subjectName}</span>;
                                                }
                                                // If new empty row, show dropdown
                                                return (
                                                    <div>
                                                        <ClassetDropdown
                                                            value={rowData.subjectId}
                                                            options={this.getAvailableSubjects(options.rowIndex)}
                                                            onChange={(e) => this.handleSubjectSelect(options.rowIndex, e.value)}
                                                            optionLabel="subjectName"
                                                            optionValue="subjectId"
                                                            placeholder="Select subject"
                                                            className={errors.subjectErrors?.[options.rowIndex]?.subjectId ? 'field-error' : ''}
                                                        />
                                                        {errors.subjectErrors?.[options.rowIndex]?.subjectId && (
                                                            <small className="error-text">
                                                                {errors.subjectErrors[options.rowIndex].subjectId}
                                                            </small>
                                                        )}
                                                    </div>
                                                );
                                            }}
                                            style={{ minWidth: '180px' }}
                                        />
                                        <Column
                                            header="Syllabus"
                                            body={(rowData, options) => (
                                                <InputText
                                                    value={rowData.syllabus}
                                                    onChange={(e) =>
                                                        this.updateSubjectField(options.rowIndex, 'syllabus', e.target.value)
                                                    }
                                                    placeholder="Enter syllabus"
                                                    disabled={!rowData.subjectId}
                                                />
                                            )}
                                            style={{ minWidth: '200px' }}
                                        />
                                        <Column
                                            header="Exam Date *"
                                            body={(rowData, options) => (
                                                <div>
                                                    <Calendar
                                                        value={rowData.examDate}
                                                        onChange={(e) =>
                                                            this.updateSubjectField(options.rowIndex, 'examDate', e.value)
                                                        }
                                                        dateFormat="dd/mm/yy"
                                                        placeholder="Select date"
                                                        showIcon
                                                        disabled={!rowData.subjectId}
                                                        className={errors.subjectErrors?.[options.rowIndex]?.examDate ? 'field-error' : ''}
                                                    />
                                                    {errors.subjectErrors?.[options.rowIndex]?.examDate && (
                                                        <small className="error-text">
                                                            {errors.subjectErrors[options.rowIndex].examDate}
                                                        </small>
                                                    )}
                                                </div>
                                            )}
                                            style={{ minWidth: '180px' }}
                                        />
                                        <Column
                                            header="Duration (min) *"
                                            body={(rowData, options) => (
                                                <InputNumber
                                                    value={rowData.examDuration}
                                                    onValueChange={(e) =>
                                                        this.updateSubjectField(options.rowIndex, 'examDuration', e.value)
                                                    }
                                                    min={1}
                                                    placeholder="Minutes"
                                                    disabled={!rowData.subjectId}
                                                />
                                            )}
                                            style={{ minWidth: '120px' }}
                                        />
                                        <Column
                                            header="Max Marks *"
                                            body={(rowData, options) => (
                                                <InputNumber
                                                    value={rowData.maxMarks}
                                                    onValueChange={(e) =>
                                                        this.updateSubjectField(options.rowIndex, 'maxMarks', e.value)
                                                    }
                                                    min={1}
                                                    placeholder="Marks"
                                                    disabled={!rowData.subjectId}
                                                />
                                            )}
                                            style={{ minWidth: '100px' }}
                                        />
                                        <Column
                                            header="Pass Mark *"
                                            body={(rowData, options) => (
                                                <div>
                                                    <InputNumber
                                                        value={rowData.passMarks}
                                                        onValueChange={(e) =>
                                                            this.updateSubjectField(options.rowIndex, 'passMarks', e.value)
                                                        }
                                                        min={0}
                                                        placeholder="Marks"
                                                        disabled={!rowData.subjectId}
                                                        className={errors.subjectErrors?.[options.rowIndex]?.passMarks ? 'field-error' : ''}
                                                    />
                                                    {errors.subjectErrors?.[options.rowIndex]?.passMarks && (
                                                        <small className="error-text">
                                                            {errors.subjectErrors[options.rowIndex].passMarks}
                                                        </small>
                                                    )}
                                                </div>
                                            )}
                                            style={{ minWidth: '100px' }}
                                        />
                                        <Column
                                            header="Marks Entry Deadline *"
                                            body={(rowData, options) => (
                                                <div>
                                                    <Calendar
                                                        value={rowData.deadlineForMarksEntry}
                                                        onChange={(e) =>
                                                            this.updateSubjectField(
                                                                options.rowIndex,
                                                                'deadlineForMarksEntry',
                                                                e.value
                                                            )
                                                        }
                                                        dateFormat="dd/mm/yy"
                                                        placeholder="Select date"
                                                        showIcon
                                                        disabled={!rowData.subjectId}
                                                        className={errors.subjectErrors?.[options.rowIndex]?.deadlineForMarksEntry ? 'field-error' : ''}
                                                    />
                                                    {errors.subjectErrors?.[options.rowIndex]?.deadlineForMarksEntry && (
                                                        <small className="error-text">
                                                            {errors.subjectErrors[options.rowIndex].deadlineForMarksEntry}
                                                        </small>
                                                    )}
                                                </div>
                                            )}
                                            style={{ minWidth: '200px' }}
                                        />
                                        <Column
                                            header="Actions"
                                            body={(rowData, options) => {
                                                const canDelete = this.canDeleteSubject(rowData.subjectId);
                                                return (
                                                    <Button
                                                        icon="pi pi-trash"
                                                        className="p-button-rounded p-button-danger p-button-text"
                                                        onClick={() => this.removeSubjectRow(options.rowIndex)}
                                                        disabled={!canDelete}
                                                        tooltip={canDelete ? "Remove subject" : "Cannot delete - marks have been entered"}
                                                        tooltipOptions={{ position: 'top' }}
                                                    />
                                                );
                                            }}
                                            style={{ width: '80px', textAlign: 'center' }}
                                        />
                                    </DataTable>
                                </div>
                            ) : (
                                <div style={{ textAlign: 'center', padding: '2rem', color: '#666', backgroundColor: '#f8f9fa', borderRadius: '4px' }}>
                                    <i className="pi pi-info-circle" style={{ fontSize: '2rem', marginBottom: '0.5rem' }}></i>
                                    <p>No subjects added yet. Click "Add Subject" button to add subjects to this examination.</p>
                                </div>
                            )}
                        </div>
                    )}

                    {errors.subjects && <div className="error-text mt-2">{errors.subjects}</div>}
                </Dialog>

                {/* Assign Exam to Students Dialog */}
                {showAssignDialog && selectedExamination && (
                    <AssignExamToStudents
                        visible={showAssignDialog}
                        onHide={this.handleAssignDialogClose}
                        examination={selectedExamination}
                        toast={this.toast}
                    />
                )}

                {/* Marks Entry Dialog */}
                {showMarksEntryDialog && selectedExamForMarks && (
                    <MarksEntry
                        visible={showMarksEntryDialog}
                        onHide={this.handleMarksEntryDialogClose}
                        examId={selectedExamForMarks.examId}
                        currentUser={this.props.userInfo}
                        onSave={this.fetchExaminations}
                    />
                )}
            </div>
        );
    }
}

const mapStatesToProps = (state) => ({
    boards: userAssignedBoards(state, 'activeBoards'),
    permissionIds: state.currentUserPerms?.permissionIds,
    userInfo: state.currentUserPerms?.userInfo
});

export default connect(mapStatesToProps, { getBoardsData })(
    Authentication(withRouter(SubjectiveExamination))
);
